import type {
  CreateRenewal,
  HrClinicCertificate,
  HrPerson,
  HrSettings,
  HrTeam,
  RenewalFile,
  RenewalMimeType,
  UpdateContract,
  UpdatePersonal,
} from "@dentacore/shared-types";

import { api } from "./client";

/**
 * HR, inside Staff — STORY-181.
 *
 * **Clinic-scoped, like time off**: a contract and a certificate belong to the
 * person, not to the location they happen to be working at.
 */
export function readTeam(signal?: AbortSignal): Promise<HrTeam> {
  return api<HrTeam>("/hr/team", { signal });
}

export function readMe(signal?: AbortSignal): Promise<HrPerson> {
  return api<HrPerson>("/hr/me", { signal });
}

export function updateContract(userId: string, body: UpdateContract): Promise<HrPerson> {
  return api<HrPerson>(`/hr/people/${userId}/contract`, { method: "PATCH", body });
}

/** Date of birth, blood type, emergency contact (STORY-192). Only what changed is sent. */
export function updatePersonal(userId: string, body: UpdatePersonal): Promise<HrPerson> {
  return api<HrPerson>(`/hr/people/${userId}/personal`, { method: "PATCH", body });
}

/** Today, one approved SICK day (HR-D8). Never your own. */
export function recordSick(userId: string): Promise<HrPerson> {
  return api<HrPerson>(`/hr/people/${userId}/sick`, { method: "POST" });
}

/** *Back at work*: removes today's sick day. */
export function backAtWork(userId: string): Promise<HrPerson> {
  return api<HrPerson>(`/hr/people/${userId}/sick`, { method: "DELETE" });
}

export function listCertificates(signal?: AbortSignal): Promise<HrClinicCertificate[]> {
  return api<HrClinicCertificate[]>("/hr/certificates", { signal });
}

/**
 * Renew a certificate, with or without a document. The document goes straight
 * to the bucket on a signed URL first; only its key comes through the API.
 */
export async function renewCertificate(
  certificateId: string,
  expiresOn: string | undefined,
  file: File | null,
): Promise<HrClinicCertificate> {
  let uploaded: RenewalFile | undefined;
  if (file !== null) {
    const mimeType = file.type as RenewalMimeType;
    const signed = await api<{ s3Key: string; uploadUrl: string }>(
      `/hr/certificates/${certificateId}/renewals/presign`,
      { method: "POST", body: { mimeType, sizeBytes: file.size } },
    );
    const put = await fetch(signed.uploadUrl, {
      method: "PUT",
      body: file,
      headers: { "content-type": mimeType },
    });
    if (!put.ok) throw new Error("The document did not upload. Try again.");
    uploaded = { s3Key: signed.s3Key, fileName: file.name, mimeType, sizeBytes: file.size };
  }
  const body: CreateRenewal = {
    ...(expiresOn === undefined ? {} : { expiresOn }),
    ...(uploaded === undefined ? {} : { file: uploaded }),
  };
  return api<HrClinicCertificate>(`/hr/certificates/${certificateId}/renewals`, {
    method: "POST",
    body,
  });
}

/** A one-minute link to a renewal's document. Open it now; do not keep it. */
export async function renewalFileUrl(renewalId: string): Promise<string> {
  return (await api<{ url: string }>(`/hr/renewals/${renewalId}/file`)).url;
}

/** Add a certificate to somebody (HR-D5): a typed name, needed to treat, an optional expiry. */
export function createCertificate(
  userId: string,
  body: { name: string; neededToTreat: boolean; expiresOn: string | null },
): Promise<HrClinicCertificate> {
  return api<HrClinicCertificate>(`/hr/people/${userId}/certificates`, { method: "POST", body });
}

/** Rename, re-tick or re-date — a correction, so the date may move either way. */
export function updateCertificate(
  certificateId: string,
  body: { name?: string; neededToTreat?: boolean; expiresOn?: string | null },
): Promise<HrClinicCertificate> {
  return api<HrClinicCertificate>(`/hr/certificates/${certificateId}`, { method: "PATCH", body });
}

/** Delete a certificate, its renewals and their documents. */
export function deleteCertificate(certificateId: string): Promise<void> {
  return api<void>(`/hr/certificates/${certificateId}`, { method: "DELETE" });
}

/** Check a waiting renewal; a date given corrects the one typed (HR-D12). */
export function checkRenewal(renewalId: string, expiresOn?: string): Promise<HrClinicCertificate> {
  return api<HrClinicCertificate>(`/hr/renewals/${renewalId}/check`, {
    method: "POST",
    body: expiresOn === undefined ? {} : { expiresOn },
  });
}

/** Reject a waiting renewal; the person sees the reason until they upload again (HR-D11). */
export function rejectRenewal(renewalId: string, reason?: string): Promise<HrClinicCertificate> {
  return api<HrClinicCertificate>(`/hr/renewals/${renewalId}/reject`, {
    method: "POST",
    body: reason === undefined || reason.trim() === "" ? {} : { reason: reason.trim() },
  });
}

/** The clinic's warning window (HR-D7), read by everyone because every Me tab flags against it. */
export function readHrSettings(signal?: AbortSignal): Promise<HrSettings> {
  return api<HrSettings>("/hr/settings", { signal });
}

/** Set it — `settings.clinic.manage`; 1–365, refused outside that by the API and the column. */
export function updateHrSettings(warnDays: number): Promise<HrSettings> {
  return api<HrSettings>("/hr/settings", { method: "PATCH", body: { warnDays } });
}
